import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2 } from 'lucide-react';
import Navbar from '../../components/brand/Navbar';
import Footer from '../../components/brand/Footer';
import FAQSection from '../../components/brand/FAQSection';

const faqs = [
  {
    q: 'Can I get a fasting blood test done at home?',
    a: 'Yes. Fasting blood tests are one of our most common requests. Simply schedule an early morning appointment, fast for 8 to 12 hours beforehand (water is fine), and our collector will perform the draw at your home. You can eat and drink normally immediately after the collection.',
  },
  {
    q: 'How long does the blood collection take?',
    a: 'The actual blood draw typically takes around 10 minutes, depending on the number of tubes required. Including the verification of your identity and pathology request form, most visits are completed within 15 minutes.',
  },
  {
    q: 'What should I do to prepare for my home blood test?',
    a: 'Have your pathology request form ready, wear a shirt with sleeves you can easily roll up, and drink plenty of water beforehand — well-hydrated veins make collection quicker and more comfortable. If your test requires fasting, stop eating 8 to 12 hours before your appointment.',
  },
  {
    q: 'How are my blood samples transported to the lab?',
    a: 'All specimens are transported in temperature-controlled conditions following NPAAC 5th Edition standards. Our collectors use insulated specimen transport containers and maintain a documented chain of custody from the moment of collection to laboratory delivery.',
  },
  {
    q: 'When will I get my results?',
    a: 'Results are sent directly from the laboratory to your referring doctor, usually within 24 to 48 hours. This is the same turnaround time as a traditional pathology collection centre. Contact your doctor\'s practice to receive your results.',
  },
  {
    q: 'Can you collect from my workplace instead of my home?',
    a: 'Yes. We can collect from any location that is convenient for you — your home, your workplace, or anywhere else within our service area across Brisbane, Sydney and Melbourne metros. Just provide the address when you book.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

const Section = ({ id, title, children }) => (
  <section
    id={id}
    className="py-16"
    style={{ borderBottom: '1px solid rgba(43, 56, 121, 0.08)' }}
  >
    <div className="container max-w-4xl mx-auto px-6">
      <h2
        className="font-['Inter'] font-bold mb-5 text-[#00001E]"
        style={{ fontSize: '1.6rem', letterSpacing: '-0.02em' }}
      >
        {title}
      </h2>
      <div className="text-[#2B3879] leading-relaxed" style={{ fontSize: '1rem' }}>
        {children}
      </div>
    </div>
  </section>
);

const InternalLink = ({ href, children }) => (
  <a href={href} className="text-[#2B3879] hover:underline font-semibold">{children}</a>
);

export default function HomeBloodCollection() {
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'view_service_page', {
        event_category: 'engagement',
        event_label: 'Home Blood Collection'
      });
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>At-Home Blood Test | Mobile Pathology Collection Brisbane, Sydney &amp; Melbourne | Nura Health</title>
        <meta name="description" content="Get your blood test done at home. Qualified, insured mobile phlebotomists come to you across Brisbane, Sydney and Melbourne metros. Fasting bloods, routine pathology & same-day available." />
        <link rel="canonical" href="https://nurahealth.com.au/services/home-blood-collection" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen" style={{ background: '#F7F9FB' }}>
        <Navbar onBookClick={() => (window.location.href = '/booking')} />

        {/* Hero — dark banner */}
        <section
          className="pt-36 pb-20 relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #00001E 0%, #0a0b2e 60%, #101a3e 100%)' }}
        >
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(ellipse at 20% 60%, rgba(128,229,203,0.09) 0%, transparent 55%)',
          }} />
          <div className="container max-w-4xl mx-auto px-6 relative z-10">
            <span className="pill-label-dark">Home Collection</span>
            <h1
              className="font-['Inter'] font-extrabold mb-5"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.03em', color: '#FFFFFF' }}
            >
              At-Home Blood Test Collection Across Brisbane, Sydney &amp; Melbourne
            </h1>
            <p className="text-lg mb-8 max-w-2xl leading-relaxed" style={{ color: 'rgba(255,255,255,0.80)' }}>
              A qualified Nura Health phlebotomist comes to your door — no waiting room, no travel, no time off work.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="/booking"
                style={{
                  display: 'inline-block',
                  background: '#80E5CB',
                  color: '#00001E',
                  borderRadius: '8px',
                  padding: '13px 28px',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  textDecoration: 'none',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  boxShadow: '0 4px 16px rgba(128,229,203,0.35)',
                }}
              >
                Book a Home Collection
              </a>
              <a
                href="tel:0420424290"
                style={{
                  display: 'inline-block',
                  background: 'transparent',
                  color: '#FFFFFF',
                  borderRadius: '8px',
                  padding: '12px 26px',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  textDecoration: 'none',
                  border: '2px solid rgba(255,255,255,0.40)',
                }}
              >
                Call 0420 424 290
              </a>
            </div>
          </div>
        </section>

        {/* Sections */}
        <Section title="Skip the Waiting Room — We Come to You">
          <p>
            Nura Health brings professional pathology collection to your home across Brisbane, Sydney and Melbourne metros.
            Whether you need routine blood work, fasting bloods, or same-day urgent collection, our qualified mobile phlebotomists
            arrive at your door with everything needed to complete your blood test safely and efficiently.
            No queues, no travel, no time off work.
          </p>
        </Section>

        <Section title="How Mobile Blood Collection Works">
          <p>
            Booking a home blood test with Nura Health takes less than two minutes. Choose your preferred date and time online,
            and one of our collectors will arrive at your home during your selected window. The collection itself takes around
            10 minutes. Your specimens are then transported in temperature-controlled conditions to our partner pathology
            laboratory for processing. Results go directly to your referring doctor, just like a traditional pathology centre.
          </p>
        </Section>

        <Section title="What Tests Can Be Done at Home">
          <p className="mb-4">
            Our mobile collectors can perform the same blood tests you would get at a walk-in collection centre. This includes:
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              'Full blood count (FBC)',
              'Liver and kidney function',
              'Cholesterol and lipid panels',
              'Thyroid function (TSH, T3, T4)',
              'Iron studies and ferritin',
              'HbA1c and glucose tolerance',
              'Hormone panels',
              'Vitamin and mineral levels',
              'Most standard GP/specialist tests',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#2B3879] flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4">If you have a pathology request form, we can collect it.</p>
        </Section>

        <Section title="Pricing">
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            {[
              { label: 'Standard Home Collection', price: '$55', note: '2+ days notice' },
              { label: 'Same-Day / Urgent Collection', price: '$75', note: 'Next day or sooner' },
            ].map(({ label, price, note }) => (
              <div
                key={label}
                className="rounded-xl p-5 bg-white"
                style={{ border: '1px solid rgba(43,56,121,0.12)', boxShadow: '0 2px 12px rgba(43,56,121,0.06)' }}
              >
                <p className="text-[#00001E] font-semibold text-lg">{price}</p>
                <p className="text-[#2B3879] font-medium text-sm">{label}</p>
                <p className="text-[#2B3879] text-xs mt-1 opacity-70">{note}</p>
              </div>
            ))}
          </div>
          <p>
            No hidden fees — the price covers the collector visit, specimen handling, and transport to the lab.
            Pathology lab processing and Medicare rebates are handled separately through the laboratory,
            exactly the same as a traditional collection centre.
          </p>
        </Section>

        <Section title="Who Uses Our Home Collection Service">
          <p>
            Our at-home blood collection service is popular with busy professionals who can't take time off work,
            parents with young children, elderly patients with limited mobility, post-surgical patients,
            people with needle anxiety who are more comfortable at home, and anyone who simply prefers
            the convenience of skipping the pathology centre queue.
          </p>
        </Section>

        <Section title="Book Your Home Blood Test">
          <p className="mb-6">
            Ready to book? It takes under two minutes online.
            We service Brisbane, Sydney and Melbourne metros.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="/booking"
              style={{
                display: 'inline-block',
                background: '#80E5CB',
                color: '#00001E',
                borderRadius: '8px',
                padding: '13px 28px',
                fontWeight: 700,
                fontSize: '0.95rem',
                textDecoration: 'none',
              }}
            >
              Book Online
            </a>
            <a
              href="tel:0420424290"
              style={{
                display: 'inline-block',
                background: 'transparent',
                color: '#2B3879',
                borderRadius: '8px',
                padding: '12px 26px',
                fontWeight: 600,
                fontSize: '0.95rem',
                textDecoration: 'none',
                border: '2px solid #2B3879',
              }}
            >
              Call 0420 424 290
            </a>
          </div>
        </Section>

        <FAQSection items={faqs} />

        {/* Internal links */}
        <section className="py-12 bg-[#F7F9FB]">
          <div className="container max-w-4xl mx-auto px-6">
            <p className="text-[#2B3879] text-sm">
              Also see:{' '}
              <InternalLink href="/services/ndis-pathology">NDIS pathology collection</InternalLink>
              {' · '}
              <InternalLink href="/services/aged-care-pathology">aged care facility services</InternalLink>
              {' · '}
              <InternalLink href="/">back to home</InternalLink>
            </p>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
